import { useState, useEffect } from "react";
import axios from "axios";
import { HOLE_COUNT, maxTotalPot } from "@/lib/game";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import CourseFinder from "@/components/CourseFinder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import {
    loadRecentCourses,
    saveRecentCourse,
    removeRecentCourse,
    findRecentCourse,
} from "@/lib/recentCourses";
import { loadRecentRounds } from "@/lib/recentRounds";
import { formatMoney } from "@/lib/game";

function TeeIcon({ className = "" }) {
    return (
        <svg
            className={className}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="12" cy="7" r="4" />
            <path d="M12 11v10" />
            <path d="M8 21h8" />
        </svg>
    );
}

function DownIcon({ open }) {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.15s ease-out",
            }}
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}

export default function SetupScreen({ onStart, onHistory }) {
    const [count, setCount] = useState(2);
    const [startingBet, setStartingBet] = useState(3);
    const [eagleEnabled, setEagleEnabled] = useState(true);
    const [players, setPlayers] = useState([
        { name: "", handicap: "" },
        { name: "", handicap: "" },
        { name: "", handicap: "" },
        { name: "", handicap: "" },
    ]);
    const [pars, setPars] = useState(new Array(HOLE_COUNT).fill(4));
    const [ratings, setRatings] = useState(
        Array.from({ length: HOLE_COUNT }, (_, i) => i + 1),
    );
    const [courseOpen, setCourseOpen] = useState(false);
    const [courseLabel, setCourseLabel] = useState(null);
    const [recents, setRecents] = useState([]);
    // Result of the latest scorecard attempt so we can show clear inline UX.
    // { kind: "preset"|"full"|"partial"|"no_data"|"rate_limited"|"error", filled?: number }
    const [courseStatus, setCourseStatus] = useState(null);
    // Last picked OSM reference so a "retry" button can rerun the scorecard fetch.
    const [lastPicked, setLastPicked] = useState(null);
    const [historyRounds, setHistoryRounds] = useState([]);

    useEffect(() => {
        setRecents(loadRecentCourses());
        setHistoryRounds(loadRecentRounds());
    }, []);

    // Auto-save the current course "preset" whenever pars/ratings change
    // (debounced) so returning to it via the recent chip — or picking it
    // again from OSM search — reloads the exact card the group used.
    useEffect(() => {
        if (!courseLabel) return;
        const t = setTimeout(() => {
            saveRecentCourse({
                courseLabel,
                pars: [...pars],
                ratings: [...ratings],
            });
            setRecents(loadRecentCourses());
        }, 700);
        return () => clearTimeout(t);
    }, [courseLabel, pars, ratings]);

    const applyCourseFromApi = async ({ courseLabel: label, osmType, osmId, location, club, city, country }) => {
        setLastPicked({ label, osmType, osmId, location, club, city, country });
        // 1. If we've configured this course before, load the saved preset.
        const preset = findRecentCourse(label);
        if (
            preset &&
            preset.pars?.length === HOLE_COUNT &&
            preset.ratings?.length === HOLE_COUNT
        ) {
            setPars([...preset.pars]);
            setRatings([...preset.ratings]);
            setCourseLabel(label);
            setCourseOpen(true);
            setCourseStatus({ kind: "preset" });
            toast.success(`Reloaded saved card for ${label}`);
            return;
        }

        // 2. Otherwise reset defaults and try to pull the real scorecard.
        const defaultPars = new Array(HOLE_COUNT).fill(4);
        const defaultRatings = Array.from({ length: HOLE_COUNT }, (_, i) => i + 1);
        setPars(defaultPars);
        setRatings(defaultRatings);
        setCourseLabel(label);
        setCourseOpen(true);

        if (!osmType || !osmId) {
            setCourseStatus({ kind: "no_data" });
            return;
        }
        await fetchScorecard({ label, osmType, osmId, location, club, city, country });
    };

    const fetchScorecard = async ({ label, osmType, osmId, location, club, city, country }) => {
        setCourseStatus({ kind: "loading" });
        const loadingId = toast.loading("Pulling scorecard…");
        try {
            const r = await axios.get(`${API}/courses/scorecard`, {
                params: {
                    osm_type: osmType,
                    osm_id: osmId,
                    ...(location?.lat != null ? { lat: location.lat } : {}),
                    ...(location?.lng != null ? { lng: location.lng } : {}),
                    ...(club ? { club } : {}),
                    ...(city ? { city } : {}),
                    ...(country ? { country } : {}),
                },
                timeout: 30000,
            });
            toast.dismiss(loadingId);
            const holes = r.data?.holes || [];
            const reason = r.data?.reason;
            if (reason === "rate_limited") {
                setCourseStatus({ kind: "rate_limited" });
                return;
            }
            const byNum = {};
            holes.forEach((h) => {
                byNum[h.hole_number] = h;
            });
            const defaultPars = new Array(HOLE_COUNT).fill(4);
            const defaultRatings = Array.from({ length: HOLE_COUNT }, (_, i) => i + 1);
            const nextPars = defaultPars.slice();
            const nextRatings = defaultRatings.slice();
            let filledPars = 0;
            let filledIdx = 0;
            for (let i = 0; i < HOLE_COUNT; i++) {
                const h = byNum[i + 1];
                if (h && h.par) {
                    nextPars[i] = h.par;
                    filledPars += 1;
                }
                if (h && h.stroke_index) {
                    nextRatings[i] = h.stroke_index;
                    filledIdx += 1;
                }
            }
            const idxSet = new Set(nextRatings);
            const goodRatings =
                filledIdx === HOLE_COUNT &&
                idxSet.size === HOLE_COUNT &&
                Math.max(...nextRatings) <= HOLE_COUNT;

            if (filledPars > 0) setPars(nextPars);
            if (goodRatings) setRatings(nextRatings);

            if (filledPars === 0) {
                setCourseStatus({ kind: "no_data" });
            } else if (filledPars < HOLE_COUNT || !goodRatings) {
                setCourseStatus({
                    kind: "partial",
                    filled: filledPars,
                    source: r.data?.source,
                });
                toast.success(
                    `Loaded ${filledPars}/18 pars${goodRatings ? " + stroke index" : ""} — fill gaps below.`,
                );
            } else {
                setCourseStatus({
                    kind: "full",
                    parTotal: nextPars.reduce((a, b) => a + b, 0),
                    source: r.data?.source,
                });
                toast.success(
                    `Loaded 18-hole scorecard · par ${nextPars.reduce((a, b) => a + b, 0)}`,
                );
            }
        } catch (e) {
            toast.dismiss(loadingId);
            setCourseStatus({ kind: "error" });
        }
    };

    const retryScorecard = () => {
        if (!lastPicked) return;
        fetchScorecard(lastPicked);
    };

    // Fast manual entry: click a par preset to fill all 18 pars at once.
    // Layouts sum to 72/71/70/68 respectively.
    const parPresets = [
        {
            label: "Par 72",
            pars: [4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 3, 4, 5, 4, 4, 3, 4, 5],
        },
        {
            label: "Par 71",
            pars: [4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 3, 4, 5, 4, 4, 3, 4, 4],
        },
        {
            label: "Par 70",
            pars: [4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 3, 4, 4, 4, 4, 3, 4, 4],
        },
        {
            label: "Par 68",
            pars: [3, 4, 4, 3, 4, 4, 4, 3, 4, 4, 4, 3, 4, 4, 4, 4, 4, 4],
        },
        {
            label: "All 4",
            pars: [4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
        },
    ];
    const applyParPreset = (preset) => {
        setPars([...preset.pars]);
        toast.success(`Filled ${preset.label} layout`);
    };

    const applyRecent = (r) => {
        setPars([...r.pars]);
        setRatings([...r.ratings]);
        setCourseLabel(r.courseLabel);
        setCourseOpen(false);
        setCourseStatus({ kind: "preset" });
        saveRecentCourse(r);
        setRecents(loadRecentCourses());
        toast.success(`Reloaded ${r.courseLabel}`);
    };

    const dropRecent = (label) => {
        removeRecentCourse(label);
        setRecents(loadRecentCourses());
    };

    const updatePlayer = (i, patch) => {
        setPlayers((prev) => {
            const next = [...prev];
            next[i] = { ...next[i], ...patch };
            return next;
        });
    };

    const setPar = (i, v) => {
        const n = Math.max(3, Math.min(6, Number(v) || 4));
        setPars((p) => {
            const arr = [...p];
            arr[i] = n;
            return arr;
        });
    };
    const setRating = (i, v) => {
        const n = Math.max(1, Math.min(18, Number(v) || 1));
        setRatings((p) => {
            const arr = [...p];
            arr[i] = n;
            return arr;
        });
    };

    const canStart = () => {
        for (let i = 0; i < count; i++) {
            if (!players[i].name.trim()) return false;
            const h = Number(players[i].handicap);
            if (Number.isNaN(h) || h < 0 || h > 54) return false;
        }
        // ratings should be 1..18 unique
        const used = new Set(ratings);
        if (used.size !== 18) return false;
        return true;
    };

    const handleStart = () => {
        // validate uniqueness of hole handicap ratings
        const used = new Set(ratings);
        if (used.size !== 18) {
            toast.error("Hole handicap ratings must be 1–18 with no repeats.");
            return;
        }
        const cfgPlayers = players
            .slice(0, count)
            .map((p) => ({ name: p.name.trim(), handicap: Number(p.handicap) }));
        if (courseLabel) {
            saveRecentCourse({
                courseLabel,
                pars: [...pars],
                ratings: [...ratings],
            });
        }
        onStart(cfgPlayers, {
            pars: [...pars],
            holeHandicapRatings: [...ratings],
            label: courseLabel,
            startingBet: Math.max(1, Math.min(100, Number(startingBet) || 3)),
            eagleEnabled,
        });
    };

    return (
        <div className="min-h-screen flex flex-col relative" data-testid="setup-screen">
            {/* Hero header */}
            <div className="relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-25"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1709525616662-8d9f9a995ceb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxnb2xmJTIwY291cnNlJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4NTY3MjYxNHww&ixlib=rb-4.1.0&q=85)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        filter: "grayscale(0.35) contrast(1.05)",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(8,9,10,0.55) 0%, rgba(8,9,10,0.95) 85%, #08090A 100%)",
                    }}
                />
                <div className="relative px-6 pt-10 pb-8 max-w-5xl mx-auto">
                    <div className="flex items-center justify-between mb-4">
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.28em] uppercase text-[color:var(--smoke)]">
                            <span className="inline-block w-1.5 h-1.5 bg-[color:var(--volt)]"></span>
                            Fairway Stakes
                        </span>
                        <button
                            onClick={onHistory}
                            className="text-[10px] uppercase tracking-[0.2em] font-[600] text-[color:var(--bone)] border border-[color:var(--line)] px-3 py-1 hover:bg-[color:var(--surface-hover)] transition-colors"
                            style={{ borderRadius: 3 }}
                            data-testid="toggle-history-button"
                        >
                            Round History
                        </button>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="font-display text-4xl sm:text-5xl lg:text-6xl font-[800]"
                        style={{ lineHeight: 0.95 }}
                    >
                        Play the round.
                        <br />
                        <span className="text-[color:var(--volt)]">
                            Bank the round.
                        </span>
                    </motion.h1>
                    <p className="mt-4 max-w-xl text-[color:var(--smoke)] text-base sm:text-lg">
                        A pocket-side wager for 2–4 golfers. Set your starting
                        bet; each outright win adds $1 to the next hole. Win
                        with handicap. Birdie it — double your take.
                        {eagleEnabled && " Eagle it — quadruple."}
                    </p>

                    <div className="mt-6 flex flex-wrap gap-2">
                        <span className="chip">
                            <span className="text-[color:var(--smoke)]">Max pot</span>
                            <span className="text-[color:var(--bone)]">
                                ${maxTotalPot(startingBet)}
                            </span>
                        </span>
                        <span className="chip">
                            <span className="text-[color:var(--smoke)]">Hole 1</span>
                            <span className="text-[color:var(--bone)]">${startingBet}</span>
                        </span>
                        <span className="chip">
                            <span className="text-[color:var(--smoke)]">Hole 18</span>
                            <span className="text-[color:var(--bone)]">${startingBet + 17}</span>
                        </span>
                        <span className="chip">
                            <span className="text-[color:var(--smoke)]">Birdie</span>
                            <span className="text-[color:var(--gold)]">×2</span>
                        </span>
                        {eagleEnabled && (
                            <span className="chip" data-testid="hero-eagle-chip">
                                <span className="text-[color:var(--smoke)]">Eagle</span>
                                <span style={{ color: "var(--eagle)" }}>×4</span>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Setup body */}
            <div className="flex-1 px-4 sm:px-6 pb-32 max-w-5xl w-full mx-auto">
                {/* Player count */}
                <section className="mt-8">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <div className="eyebrow">01 · Field</div>
                            <h2 className="font-display text-2xl font-[700] mt-1">
                                How many in the group?
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {[2, 3, 4].map((n) => (
                            <button
                                key={n}
                                data-testid={`player-count-${n}`}
                                onClick={() => setCount(n)}
                                className={`py-4 border transition-colors ${
                                    count === n
                                        ? "bg-[color:var(--volt)] text-[#0a0b0c] border-[color:var(--volt)]"
                                        : "border-[color:var(--line-strong)] text-[color:var(--bone)] hover:border-[color:var(--volt)] hover:text-[color:var(--volt)]"
                                }`}
                                style={{
                                    borderRadius: 3,
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 800,
                                }}
                            >
                                <div className="font-mono text-2xl">{n}</div>
                                <div className="text-[10px] tracking-[0.24em] uppercase mt-1 opacity-70">
                                    Players
                                </div>
                            </button>
                        ))}
                    </div>
                </section>

                {/* Players */}
                <section className="mt-10">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <div className="eyebrow">02 · Roster</div>
                            <h2 className="font-display text-2xl font-[700] mt-1">
                                Name & handicap
                            </h2>
                        </div>
                    </div>
                    <div className="grid gap-3">
                        {Array.from({ length: count }).map((_, i) => (
                            <div
                                key={`player-${i}`}
                                className="surface hairline p-4 grid grid-cols-[auto_1fr_120px] gap-3 items-center"
                                style={{ borderRadius: 3 }}
                                data-testid={`player-row-${i}`}
                            >
                                <div
                                    className="w-10 h-10 flex items-center justify-center border hairline-strong font-mono text-sm text-[color:var(--volt)]"
                                    style={{ borderRadius: 3 }}
                                >
                                    P{i + 1}
                                </div>
                                <div>
                                    <Label
                                        className="eyebrow"
                                        htmlFor={`name-${i}`}
                                    >
                                        Name
                                    </Label>
                                    <Input
                                        id={`name-${i}`}
                                        data-testid={`player-name-input-${i}`}
                                        value={players[i].name}
                                        onChange={(e) =>
                                            updatePlayer(i, {
                                                name: e.target.value,
                                            })
                                        }
                                        placeholder={`Golfer ${i + 1}`}
                                        className="bg-[#0f1113] border-[color:var(--line-strong)] mt-1 font-display text-base h-11"
                                    />
                                </div>
                                <div>
                                    <Label
                                        className="eyebrow"
                                        htmlFor={`hcp-${i}`}
                                    >
                                        Handicap
                                    </Label>
                                    <Input
                                        id={`hcp-${i}`}
                                        data-testid={`player-handicap-input-${i}`}
                                        type="number"
                                        inputMode="numeric"
                                        min={0}
                                        max={54}
                                        value={players[i].handicap}
                                        onChange={(e) =>
                                            updatePlayer(i, {
                                                handicap: e.target.value,
                                            })
                                        }
                                        placeholder="0"
                                        className="bg-[#0f1113] border-[color:var(--line-strong)] mt-1 font-mono text-lg h-11 text-center"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Wager */}
                <section className="mt-10">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <div className="eyebrow">03 · Wager</div>
                            <h2 className="font-display text-2xl font-[700] mt-1">
                                Starting bet
                            </h2>
                            <p className="text-sm text-[color:var(--smoke)] mt-1">
                                Hole 1 begins at{" "}
                                <span className="font-mono text-[color:var(--volt)]">
                                    ${startingBet}
                                </span>
                                . Each outright win adds $1 to the next hole
                                (hole 18 tops at{" "}
                                <span className="font-mono text-[color:var(--bone)]">
                                    ${startingBet + 17}
                                </span>
                                ).
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                            const active = Number(startingBet) === n;
                            return (
                                <button
                                    key={n}
                                    onClick={() => setStartingBet(n)}
                                    data-testid={`starting-bet-${n}`}
                                    className={`px-3 py-2 font-mono text-sm border transition-colors ${
                                        active
                                            ? "bg-[color:var(--volt)] text-[#0a0b0c] border-[color:var(--volt)]"
                                            : "hairline-strong text-[color:var(--bone)] hover:border-[color:var(--volt)] hover:text-[color:var(--volt)]"
                                    }`}
                                    style={{ borderRadius: 3, minWidth: 44 }}
                                >
                                    ${n}
                                </button>
                            );
                        })}
                        <div className="ml-2 flex items-center gap-2">
                            <span className="eyebrow">or custom</span>
                            <input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={100}
                                value={startingBet}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === "") {
                                        setStartingBet("");
                                        return;
                                    }
                                    const n = Math.max(1, Math.min(100, Number(v) || 1));
                                    setStartingBet(n);
                                }}
                                onBlur={(e) => {
                                    if (e.target.value === "") setStartingBet(3);
                                }}
                                data-testid="starting-bet-custom"
                                className="fs-input-sm"
                                style={{ width: 76, textAlign: "center" }}
                            />
                        </div>
                    </div>

                    {/* Eagle Bonus toggle */}
                    <div
                        className="mt-4 surface hairline p-3 sm:p-4 flex items-start justify-between gap-4"
                        style={{
                            borderRadius: 3,
                            borderLeft: eagleEnabled
                                ? "3px solid var(--eagle)"
                                : "1px solid var(--line)",
                        }}
                        data-testid="eagle-toggle-row"
                    >
                        <div>
                            <div className="flex items-center gap-2">
                                <span
                                    className="font-display font-[700] text-sm sm:text-base uppercase tracking-wide"
                                    style={{
                                        color: eagleEnabled ? "var(--eagle)" : "var(--bone)",
                                    }}
                                >
                                    Eagle Bonus
                                </span>
                                <span
                                    className="chip"
                                    style={{
                                        background: eagleEnabled
                                            ? "rgba(110,231,249,0.14)"
                                            : "rgba(253,251,247,0.06)",
                                        color: eagleEnabled ? "var(--eagle)" : "var(--smoke)",
                                    }}
                                >
                                    ×4
                                </span>
                            </div>
                            <p className="text-xs text-[color:var(--smoke)] mt-1.5 max-w-md">
                                Winner cards a natural eagle (2 under par or
                                better) → the hole pays out quadruple. Turn
                                off if you're keeping it strictly birdie-only.
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={eagleEnabled}
                            onClick={() => setEagleEnabled((v) => !v)}
                            data-testid="eagle-toggle"
                            className="shrink-0 mt-1"
                            style={{
                                width: 46,
                                height: 26,
                                borderRadius: 999,
                                background: eagleEnabled
                                    ? "var(--eagle)"
                                    : "rgba(253,251,247,0.16)",
                                position: "relative",
                                transition: "background-color 0.15s ease-out",
                                cursor: "pointer",
                                border: "none",
                                padding: 0,
                            }}
                        >
                            <span
                                aria-hidden="true"
                                style={{
                                    display: "block",
                                    width: 20,
                                    height: 20,
                                    borderRadius: 999,
                                    background: "#0a0b0c",
                                    position: "absolute",
                                    top: 3,
                                    left: eagleEnabled ? 23 : 3,
                                    transition: "left 0.18s ease-out",
                                }}
                            />
                        </button>
                    </div>
                </section>

                {/* Course Finder */}
                <section className="mt-10">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <div className="eyebrow">04 · Course</div>
                            <h2 className="font-display text-2xl font-[700] mt-1">
                                Find your course
                            </h2>
                            <p className="text-sm text-[color:var(--smoke)] mt-1">
                                Powered by OpenStreetMap. Search by name, city, or
                                tap{" "}
                                <span className="text-[color:var(--bone)]">
                                    Near me
                                </span>
                                . Set par & stroke index below.
                            </p>
                        </div>
                    </div>
                    <CourseFinder onApply={applyCourseFromApi} />
                    {recents.length > 0 && (
                        <div className="mt-3" data-testid="recent-courses-strip">
                            <div className="eyebrow mb-2">Recent</div>
                            <div className="flex flex-wrap gap-2">
                                <AnimatePresence initial={false}>
                                    {recents.map((r) => {
                                        const parTotal = Array.isArray(r.pars)
                                            ? r.pars.reduce(
                                                  (a, b) => a + (Number(b) || 0),
                                                  0,
                                              )
                                            : 0;
                                        return (
                                            <motion.div
                                                key={r.courseLabel}
                                                initial={{ opacity: 0, y: 4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, scale: 0.94 }}
                                                transition={{ duration: 0.15 }}
                                                className="inline-flex items-stretch surface hairline overflow-hidden"
                                                style={{ borderRadius: 3 }}
                                            >
                                                <button
                                                    onClick={() => applyRecent(r)}
                                                    data-testid={`recent-course-${r.courseLabel}`}
                                                    className="px-3 py-2 text-xs uppercase tracking-[0.1em] font-display text-[color:var(--bone)] hover:text-[color:var(--volt)] transition-colors flex items-center gap-2"
                                                >
                                                    <span>{r.courseLabel}</span>
                                                    {parTotal > 0 && (
                                                        <span
                                                            className="font-mono text-[10px] tracking-[0.16em] text-[color:var(--volt)] px-1.5 py-0.5"
                                                            style={{
                                                                border: "1px solid rgba(204,255,0,0.35)",
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            PAR {parTotal}
                                                        </span>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        dropRecent(r.courseLabel)
                                                    }
                                                    aria-label={`Remove ${r.courseLabel}`}
                                                    data-testid={`recent-remove-${r.courseLabel}`}
                                                    className="px-2 border-l hairline text-[color:var(--smoke)] hover:text-[color:var(--danger)] transition-colors text-sm"
                                                >
                                                    ×
                                                </button>
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>
                            </div>
                        </div>
                    )}
                    {courseLabel && (() => {
                        const kind = courseStatus?.kind || "loading";
                        const parTotal = pars.reduce((a, b) => a + Number(b || 0), 0);
                        const sourceLabel =
                            courseStatus?.source === "golfapi.io"
                                ? "GolfAPI.io"
                                : courseStatus?.source === "osm"
                                  ? "OpenStreetMap"
                                  : null;
                        const statusText = {
                            preset: "Reloaded saved card",
                            full: `Loaded 18-hole scorecard · par ${courseStatus?.parTotal ?? parTotal}${sourceLabel ? ` · via ${sourceLabel}` : ""}`,
                            partial: `${courseStatus?.filled ?? 0} of 18 pars loaded${sourceLabel ? ` via ${sourceLabel}` : ""} — quick-fill or set the rest below`,
                            no_data: "No scorecard data found for this course. Pick a layout below or set each hole.",
                            rate_limited: "Scorecard source is busy. Retry, or set par manually below.",
                            error: "Couldn't reach the scorecard source. Retry, or set par manually below.",
                            loading: "Fetching scorecard…",
                        }[kind];
                        const accent = {
                            preset: "var(--volt)",
                            full: "var(--volt)",
                            partial: "var(--gold)",
                            no_data: "var(--gold)",
                            rate_limited: "var(--danger)",
                            error: "var(--danger)",
                            loading: "var(--smoke)",
                        }[kind];
                        const showRetry = kind === "rate_limited" || kind === "error";
                        const showQuickFill =
                            kind === "no_data" ||
                            kind === "partial" ||
                            kind === "rate_limited" ||
                            kind === "error";
                        return (
                            <div
                                className="mt-3 surface hairline"
                                style={{
                                    borderRadius: 3,
                                    borderLeft: `3px solid ${accent}`,
                                }}
                                data-testid="loaded-course-badge"
                            >
                                <div className="flex items-start justify-between gap-3 p-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="eyebrow">Course</div>
                                        <div
                                            className="font-display text-sm sm:text-base uppercase tracking-wide mt-0.5 truncate"
                                            data-testid="loaded-course-label"
                                        >
                                            {courseLabel}
                                        </div>
                                        <div
                                            className="text-xs mt-1.5 text-[color:var(--smoke)]"
                                            data-testid="course-status-text"
                                            style={{ color: accent === "var(--volt)" ? "var(--volt)" : accent === "var(--danger)" ? "var(--danger)" : "var(--smoke)" }}
                                        >
                                            {statusText}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {showRetry && (
                                            <button
                                                onClick={retryScorecard}
                                                data-testid="retry-scorecard-button"
                                                className="text-xs uppercase tracking-[0.2em] text-[color:var(--volt)] hover:text-[color:var(--bone)]"
                                            >
                                                Retry
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setCourseLabel(null);
                                                setCourseStatus(null);
                                                setLastPicked(null);
                                                setPars(new Array(HOLE_COUNT).fill(4));
                                                setRatings(
                                                    Array.from(
                                                        { length: HOLE_COUNT },
                                                        (_, i) => i + 1,
                                                    ),
                                                );
                                            }}
                                            className="text-xs uppercase tracking-[0.2em] text-[color:var(--smoke)] hover:text-[color:var(--bone)]"
                                            data-testid="clear-loaded-course"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                                {showQuickFill && (
                                    <div
                                        className="px-3 pb-3 flex flex-wrap gap-2 border-t hairline pt-3"
                                        data-testid="par-quick-fill"
                                    >
                                        <div className="w-full eyebrow mb-1">
                                            Quick-fill layout
                                        </div>
                                        {parPresets.map((p) => (
                                            <button
                                                key={p.label}
                                                onClick={() => applyParPreset(p)}
                                                data-testid={`quick-fill-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                                                className="px-3 py-1.5 text-xs uppercase tracking-[0.1em] font-display hairline-strong hover:border-[color:var(--volt)] hover:text-[color:var(--volt)] transition-colors"
                                                style={{ borderRadius: 3 }}
                                            >
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </section>

                {/* Course (collapsible) */}
                <section className="mt-10">
                    <button
                        onClick={() => setCourseOpen((v) => !v)}
                        data-testid="toggle-course-settings"
                        className="w-full flex items-center justify-between py-4 border-b hairline"
                    >
                        <div className="text-left">
                            <div className="eyebrow">05 · Fine-tune</div>
                            <h2 className="font-display text-2xl font-[700] mt-1">
                                Par & stroke index{" "}
                                <span className="text-[color:var(--smoke)] text-sm font-normal">
                                    (optional)
                                </span>
                            </h2>
                        </div>
                        <div className="text-[color:var(--smoke)] flex items-center gap-2 text-sm">
                            {courseOpen ? "Hide" : "Edit"}
                            <DownIcon open={courseOpen} />
                        </div>
                    </button>
                    {courseOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.15 }}
                            className="mt-4"
                        >
                            <p className="text-sm text-[color:var(--smoke)] mb-3">
                                Set par (3–6) and stroke index (1 = hardest, 18
                                = easiest) for each hole. Defaults: par 4,
                                index = hole number.
                            </p>
                            <div className="overflow-x-auto pb-2 hairline surface" style={{ borderRadius: 3 }}>
                                <table className="w-full font-mono text-xs">
                                    <thead className="text-[color:var(--smoke)]">
                                        <tr>
                                            <th className="text-left px-3 py-2">
                                                Hole
                                            </th>
                                            {Array.from({ length: 18 }).map((_, i) => (
                                                <th
                                                    key={`hole-${i}`}
                                                    className="px-1 py-2 text-center"
                                                >
                                                    {i + 1}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="px-3 py-2 text-[color:var(--smoke)] eyebrow">
                                                Par
                                            </td>
                                            {pars.map((p, i) => (
                                                <td key={`par-${i}`} className="px-1 py-1">
                                                    <input
                                                        type="number"
                                                        min={3}
                                                        max={6}
                                                        value={p}
                                                        onChange={(e) =>
                                                            setPar(i, e.target.value)
                                                        }
                                                        data-testid={`par-input-${i}`}
                                                        className="fs-input-sm"
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td className="px-3 py-2 text-[color:var(--smoke)] eyebrow">
                                                Idx
                                            </td>
                                            {ratings.map((r, i) => (
                                                <td key={`rating-${i}`} className="px-1 py-1">
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={18}
                                                        value={r}
                                                        onChange={(e) =>
                                                            setRating(i, e.target.value)
                                                        }
                                                        data-testid={`rating-input-${i}`}
                                                        className="fs-input-sm"
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td className="px-3 py-2 text-[color:var(--smoke)] eyebrow">
                                                Max bet
                                            </td>
                                            {Array.from({ length: 18 }).map((_, i) => (
                                                <td
                                                    key={`maxbet-${i}`}
                                                    className="px-1 py-2 text-center text-[color:var(--volt)]"
                                                >
                                                    ${3 + i}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    )}
                </section>

                {/* Rules dropdown */}
                <section className="mt-10">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="rules-menu-trigger"
                                className="btn-ghost text-xs"
                            >
                                How it plays →
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="start"
                            className="bg-[color:var(--surface)] border-[color:var(--line-strong)] text-[color:var(--bone)] font-body w-72"
                            style={{ borderRadius: 3 }}
                        >
                            <DropdownMenuLabel className="eyebrow">
                                Rules of the round
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-[color:var(--line)]" />
                            <DropdownMenuItem className="text-sm focus:bg-[color:var(--surface-2)]">
                                Hole 1 = ${startingBet} · +$1 after every outright win
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-sm focus:bg-[color:var(--surface-2)]">
                                Lowest net score wins the pot
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-sm focus:bg-[color:var(--surface-2)]">
                                Tied? Hole voided · next hole stays the same $
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-sm text-[color:var(--gold)] focus:bg-[color:var(--surface-2)]">
                                Winner made a birdie → 2× payout
                            </DropdownMenuItem>
                            {eagleEnabled && (
                                <DropdownMenuItem
                                    className="text-sm focus:bg-[color:var(--surface-2)]"
                                    style={{ color: "var(--eagle)" }}
                                >
                                    Winner made an eagle → 4× payout
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </section>
            </div>

            {/* Sticky footer CTA */}
            <div
                className="fixed bottom-0 left-0 right-0 z-40 border-t"
                style={{
                    borderColor: "var(--line)",
                    background: "rgba(8,9,10,0.94)",
                    backdropFilter: "blur(8px)",
                }}
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <div>
                        <div className="eyebrow">Max pot</div>
                        <div className="font-mono text-2xl text-[color:var(--volt)]">
                            ${maxTotalPot(startingBet)}
                        </div>
                    </div>
                    <Button
                        onClick={handleStart}
                        disabled={!canStart()}
                        data-testid="start-round-button"
                        className="btn-volt"
                    >
                        <TeeIcon className="mr-2" /> Tee off
                    </Button>
                </div>
            </div>
        </div>
    );
}
